import { useState, useCallback, useRef } from "react";
import { useToast } from "../../../../../../components/toast";
import { uploadChatAttachment } from "../../../../../../services/conversations";

const MAX_FILES = 10;

export const useChatAttachments = ({
  agentUuid,
  conversationUuid,
  createNewConversation,
} = {}) => {
  const [attachments, setAttachments] = useState([]);
  const toast = useToast();
  const didAddRef = useRef(false);

  const uploadFile = useCallback(
    async (file) => {
      try {
        let currentConversationUuid = conversationUuid;

        if (!currentConversationUuid && agentUuid && createNewConversation) {
          try {
            const newConversation = await createNewConversation(agentUuid, {
              selectAfterCreate: true,
            });
            currentConversationUuid = newConversation.conversationUuid || newConversation.id;
          } catch (createErr) {
            console.error("Failed to create conversation before file upload:", createErr);
            toast.error({
              message: "Failed to create conversation. Please try again.",
            });
            throw createErr;
          }
        }

        if (!currentConversationUuid) {
          throw new Error("No conversation available for file upload");
        }

        const result = await uploadChatAttachment(file, {
          agentUuid,
          conversationUuid: currentConversationUuid,
        });
        return result;
      } catch (err) {
        console.error("Chat attachment upload failed:", err);
        throw err;
      }
    },
    [agentUuid, conversationUuid, createNewConversation, toast],
  );

  const handleFilesSelected = useCallback(
    (files) => {
      if (!files || files.length === 0) return;
      const fileList = Array.from(files);
      didAddRef.current = false;
      const newAttachments = fileList.map((file, i) => {
        const id = `${Date.now()}-${i}`;
        const isImage = file.type?.startsWith?.("image/") ?? false;
        return isImage
          ? {
              id,
              type: "image",
              imageUrl: URL.createObjectURL(file),
              isLoading: true,
            }
          : {
              id,
              type: "file",
              name: file.name ?? "document",
              fileType: file.name?.includes?.(".")
                ? file.name.split(".").pop()
                : (file.type ?? ""),
              isLoading: true,
            };
      });
      setAttachments((prev) => {
        const total = prev.length + newAttachments.length;
        if (total > MAX_FILES) {
          toast.error({ message: "You cannot upload more than 10 files." });
          return prev;
        }
        didAddRef.current = true;
        return [...prev, ...newAttachments];
      });
      setTimeout(() => {
        if (!didAddRef.current) return;
        fileList.forEach((file, i) => {
          const att = newAttachments[i];
          if (!att) return;
          uploadFile(file)
            .then((result) => {
              const fileUuid = result?.fileUuid ?? result;
              const name = result?.name ?? att.name;
              const fileType = result?.fileType ?? att.fileType;
              setAttachments((prev) =>
                prev.map((a) =>
                  a.id === att.id
                    ? {
                        ...a,
                        isLoading: false,
                        fileUuid: fileUuid ?? a.fileUuid,
                        name: name ?? a.name,
                        fileType: fileType ?? a.fileType,
                      }
                    : a,
                ),
              );
            })
            .catch(() => {
              setAttachments((prev) =>
                prev.map((a) =>
                  a.id === att.id ? { ...a, isLoading: false } : a,
                ),
              );
            });
        });
      }, 0);
    },
    [uploadFile, toast],
  );

  const handleAttachmentRemove = useCallback((id) => {
    setAttachments((prev) => {
      const removed = prev.find((a) => a.id === id);
      if (
        removed?.type === "image" &&
        removed?.imageUrl?.startsWith?.("blob:")
      ) {
        URL.revokeObjectURL(removed.imageUrl);
      }
      return prev.filter((a) => a.id !== id);
    });
  }, []);

  const clearAttachments = useCallback(() => {
    setAttachments((prev) => {
      prev.forEach((a) => {
        if (a.type === "image" && a.imageUrl?.startsWith?.("blob:")) {
          URL.revokeObjectURL(a.imageUrl);
        }
      });
      return [];
    });
  }, []);

  const handleAddDocumentsFromDrive = useCallback(
    (selectedDocuments) => {
      if (!selectedDocuments?.length) return;
      const newAttachments = selectedDocuments.map((doc) => {
        const documentUuid = doc.document_uuid ?? doc.id;
        const name = doc.name ?? "document";
        const fileType = name?.includes?.(".")
          ? name.split(".").pop()
          : "";
        return {
          id: documentUuid,
          type: "file",
          name,
          fileType,
          isLoading: false,
          document_uuid: documentUuid,
        };
      });
      setAttachments((prev) => {
        const total = prev.length + newAttachments.length;
        if (total > MAX_FILES) {
          toast.error({ message: "You cannot add more than 10 files." });
          return prev;
        }
        return [...prev, ...newAttachments];
      });
    },
    [toast]
  );

  const handleAddMenuChange = useCallback((value, itemData) => {
    if (value !== "upload") {
      console.log("Seleccionaste:", itemData?.label ?? value, value);
    }
  }, []);

  const handleModeMenuChange = useCallback(
    (modeValue, selectedValue, itemData) => {
      console.log("Modo:", modeValue, "Selección:", selectedValue, itemData);
    },
    [],
  );

  return {
    attachments,
    handleFilesSelected,
    handleAttachmentRemove,
    handleAddDocumentsFromDrive,
    clearAttachments,
    handleAddMenuChange,
    handleModeMenuChange,
  };
};
